import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, MenuItem, Autocomplete,
    CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, useTheme, useMediaQuery, Tabs, Tab, IconButton,
    Table, TableHead, TableRow, TableCell, TableBody, Chip, Stack,
    FormControlLabel, Switch, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';
import api from '../services/api';
import BuscaPaciente from './BuscaPaciente';
import { isVisualizador } from '../services/auth';
import { toastSuccess, alertValidacao, confirmAcao } from '../services/alerts';

const STATUS_OPCOES = [
    { v: 'ATIVO', l: 'Ativo' },
    { v: 'INATIVO', l: 'Inativo' },
    { v: 'ENCERRADO', l: 'Encerrado' },
];

const STATUS_TRATAMENTO_OPCOES = [
    { v: 'ATIVO',    l: 'Ativo',    color: '#16a34a', bg: '#dcfce7', desc: 'Paciente em tratamento regular.' },
    { v: 'PAUSADO',  l: 'Pausado',  color: '#2563eb', bg: '#dbeafe', desc: 'Tratamento temporariamente interrompido.' },
    { v: 'ALTA',     l: 'Alta',     color: '#b45309', bg: '#fef9c3', desc: 'Paciente recebeu alta clínica.' },
    { v: 'EVADIDO',  l: 'Evadido',  color: '#dc2626', bg: '#fee2e2', desc: 'Paciente abandonou o tratamento.' },
];

const estadoInicial = () => ({
    idPaciente: null,
    numeroRegistro: '',
    status: 'ATIVO',
    cid: { codigo: '', descricao: '' },
});

const SectionTitle = ({ children, descricao }) => (
    <Box sx={{ mb: descricao ? 2 : 3 }}>
        <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
            {children}
        </Typography>
        {descricao && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1.5 }}>
                {descricao}
            </Typography>
        )}
    </Box>
);

export default function ProntuarioForm() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const isView = searchParams.get('view') === '1';

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const soLeitura = isView || isVisualizador();

    const [tab, setTab] = useState(0);
    const [form, setForm] = useState(estadoInicial());
    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    const [dataAbertura, setDataAbertura] = useState(null);
    const [evolucoes, setEvolucoes] = useState([]);
    const [possuiAnamnese, setPossuiAnamnese] = useState(false);
    const [idAnamnese, setIdAnamnese] = useState(null);
    const [fetching, setFetching] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [evolucaoView, setEvolucaoView] = useState(null);
    const [statusTratamento, setStatusTratamento] = useState('');
    const [statusTratamentoInicial, setStatusTratamentoInicial] = useState('');
    const [salvandoStatus, setSalvandoStatus] = useState(false);


    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/prontuarios/${id}`);
                setForm({
                    idPaciente: data.idPaciente,
                    numeroRegistro: data.numeroRegistro || '',
                    status: data.status || 'ATIVO',
                    cid: data.cid ? { codigo: data.cid.codigo || '', descricao: data.cid.descricao || '' } : { codigo: '', descricao: '' },
                });
                setPacienteSelecionado({ id_paciente: data.idPaciente, nomeCompleto: data.nomePaciente });
                setDataAbertura(data.dataAbertura);
                setEvolucoes(data.evolucoes || []);
                setPossuiAnamnese(!!data.possuiAnamnese);
                setIdAnamnese(data.idAnamnese || null);
                if (data.idPaciente) {
                    try {
                        const pac = await api.get(`/pacientes/${data.idPaciente}`);
                        const st = pac.data?.statusTratamento || 'ATIVO';
                        setStatusTratamento(st);
                        setStatusTratamentoInicial(st);
                    } catch { /* ignore */ }
                }
            } catch {
                /* erro exibido pelo handler global */
            } finally {
                setFetching(false);
            }
        })();
    }, [id, isEdit]);

    const setCampo = (campo, valor) => setForm(prev => ({ ...prev, [campo]: valor }));
    // Busca no catálogo de CID, carregado por migration: CID-10 do DATASUS, em
    // português, e CID-11 da linearização MMS da OMS, em inglês — a OMS só
    // distribui a tabulação estática nesse idioma.
    const [cidOpcoes, setCidOpcoes] = useState([]);
    const [cidBusca, setCidBusca] = useState('');
    const [cidCarregando, setCidCarregando] = useState(false);
    const [cidVersao, setCidVersao] = useState('CID10');
    const [cidSoTranstornos, setCidSoTranstornos] = useState(true);

    // Capítulo dos transtornos mentais em cada classificação: "F" no CID-10,
    // "06" no CID-11.
    const capituloMental = cidVersao === 'CID11' ? '06' : 'F';

    useEffect(() => {
        let ativo = true;
        setCidCarregando(true);
        // Debounce: a busca dispara a cada tecla digitada.
        const t = setTimeout(() => {
            api.get('/cid', {
                params: {
                    termo: cidBusca || undefined,
                    versao: cidVersao,
                    capitulo: cidSoTranstornos ? capituloMental : undefined,
                    limite: 30,
                },
            })
                .then(r => { if (ativo) setCidOpcoes(Array.isArray(r.data) ? r.data : []); })
                .catch(() => { if (ativo) setCidOpcoes([]); })
                .finally(() => { if (ativo) setCidCarregando(false); });
        }, 300);
        return () => { ativo = false; clearTimeout(t); };
    }, [cidBusca, cidVersao, cidSoTranstornos, capituloMental]);

    const solicitarSalvar = async () => {
        if (!isEdit && !form.idPaciente) {
            alertValidacao('Selecione um paciente.');
            return;
        }
        const ok = await confirmAcao({
            title: 'Confirmar Alterações',
            text: 'Deseja confirmar e gravar os dados deste prontuário?',
            confirmButtonText: 'Confirmar e Salvar',
            cancelButtonText: 'Rever Dados',
            icon: 'question',
        });
        if (ok) executarSalvar();
    };

    const executarSalvar = async () => {
        setSaving(true);
        try {
            if (isEdit) {
                const { data } = await api.put(`/prontuarios/${id}`, form);
                toastSuccess('Prontuário atualizado com sucesso!');
                if (data) {
                    setForm({
                        idPaciente: data.idPaciente,
                        numeroRegistro: data.numeroRegistro || '',
                        status: data.status || 'ATIVO',
                        cid: data.cid ? { codigo: data.cid.codigo || '', descricao: data.cid.descricao || '' } : { codigo: '', descricao: '' },
                    });
                    setDataAbertura(data.dataAbertura);
                    setEvolucoes(data.evolucoes || []);
                    setPossuiAnamnese(!!data.possuiAnamnese);
                    setIdAnamnese(data.idAnamnese || null);
                }
            } else {
                const { data } = await api.post('/prontuarios', form);
                toastSuccess('Prontuário cadastrado com sucesso!');
                if (data?.id) navigate(`/prontuarios/${data.id}`, { replace: true });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const salvarStatusTratamento = async () => {
        if (!form.idPaciente || !statusTratamento) return;
        if (statusTratamento === statusTratamentoInicial) {
            alertValidacao('Selecione um status diferente do atual.');
            return;
        }
        const opc = STATUS_TRATAMENTO_OPCOES.find(o => o.v === statusTratamento);
        const ok = await confirmAcao({
            title: 'Atualizar Status de Tratamento',
            text: `Confirmar mudança para "${opc?.l || statusTratamento}"?`,
            confirmButtonText: 'Atualizar',
            cancelButtonText: 'Cancelar',
            icon: 'question',
        });
        if (!ok) return;
        setSalvandoStatus(true);
        try {
            await api.patch(`/pacientes/${form.idPaciente}/status-tratamento`, { status: statusTratamento });
            setStatusTratamentoInicial(statusTratamento);
            toastSuccess('Status de tratamento atualizado!');
        } catch (err) {
            console.error(err);
        } finally {
            setSalvandoStatus(false);
        }
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    const formatarData = (iso) => iso ? new Date(iso).toLocaleString('pt-BR') : '-';

    const SecaoDados = (
        <Box>
            <SectionTitle descricao="Dados gerais do prontuário do paciente.">Dados Gerais</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField fullWidth label="Número de Registro" disabled={soLeitura}
                        helperText={!isEdit ? "Deixe vazio para gerar automaticamente" : ""}
                        value={form.numeroRegistro}
                        onChange={(e) => setCampo('numeroRegistro', e.target.value)} />
                    <TextField select fullWidth label="Status" disabled={soLeitura}
                        value={form.status}
                        onChange={(e) => setCampo('status', e.target.value)}>
                        {STATUS_OPCOES.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                    </TextField>
                </Box>
                {isEdit && (
                    <TextField fullWidth label="Data de Abertura" disabled value={formatarData(dataAbertura)} />
                )}
            </Box>
        </Box>
    );

    const SecaoCid = (
        <Box>
            <SectionTitle descricao="Diagnóstico do catálogo CID-10. Busque por código ou descrição.">CID</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Autocomplete
                    options={cidOpcoes}
                    loading={cidCarregando}
                    disabled={soLeitura}
                    getOptionLabel={(o) => (o?.codigo ? `${o.codigo} — ${o.descricao}` : '')}
                    isOptionEqualToValue={(o, v) => o.codigo === v.codigo}
                    // O valor vem do form; sem codigo nao ha selecao.
                    value={form.cid.codigo ? { codigo: form.cid.codigo, descricao: form.cid.descricao } : null}
                    onChange={(_, val) => setForm(prev => ({
                        ...prev,
                        cid: val
                            ? { codigo: val.codigo, descricao: val.descricao, versao: val.versao || 'CID10' }
                            : { codigo: '', descricao: '' },
                    }))}
                    onInputChange={(_, val, motivo) => { if (motivo === 'input') setCidBusca(val); }}
                    // A lista ja vem filtrada do back-end; filtrar de novo esconderia resultados.
                    filterOptions={(x) => x}
                    noOptionsText={cidBusca ? 'Nenhum código encontrado' : 'Digite para buscar'}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="CID"
                            placeholder="Ex.: F32.1 ou depressão"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {cidCarregando ? <CircularProgress size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                    {/* A troca limpa a lista: código de uma classificação não
                        existe na outra, e manter o resultado anterior na tela
                        sugeriria que existe. */}
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={cidVersao}
                        disabled={soLeitura}
                        onChange={(_e, v) => { if (v) { setCidVersao(v); setCidOpcoes([]); } }}
                        aria-label="Classificação"
                    >
                        <ToggleButton value="CID10" sx={{ px: 1.5 }}>CID-10</ToggleButton>
                        <ToggleButton value="CID11" sx={{ px: 1.5 }}>CID-11</ToggleButton>
                    </ToggleButtonGroup>

                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={cidSoTranstornos}
                                disabled={soLeitura}
                                onChange={(e) => setCidSoTranstornos(e.target.checked)}
                            />
                        }
                        label={`Somente transtornos mentais e comportamentais (capítulo ${capituloMental})`}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: 'text.secondary' } }}
                    />
                </Stack>

                {cidVersao === 'CID11' && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Os títulos do CID-11 estão em inglês: a OMS distribui a tabulação
                        oficial apenas nesse idioma.
                    </Typography>
                )}
            </Box>
        </Box>
    );

    const SecaoAnamnese = (
        <Box>
            <SectionTitle descricao="Anamnese vinculada a este prontuário.">Anamnese</SectionTitle>
            {possuiAnamnese ? (
                <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                    <Chip color="success" label="Anamnese preenchida" />
                    <Button variant="outlined" onClick={() => navigate(`/anamnese/${idAnamnese}`)}
                        sx={{ color: '#1ABB9C', borderColor: '#1ABB9C' }}>
                        Abrir Anamnese
                    </Button>
                </Stack>
            ) : (
                <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                    <Chip label="Sem anamnese" />
                    {!soLeitura && form.idPaciente && (
                        <Button variant="outlined" onClick={() => navigate('/anamnese/novo')}
                            sx={{ color: '#1ABB9C', borderColor: '#1ABB9C' }}>
                            Criar Anamnese
                        </Button>
                    )}
                </Stack>
            )}
        </Box>
    );

    const SecaoTratamento = (
        <Box>
            <SectionTitle descricao="Status atual do tratamento do paciente. Mudanças são manuais e ficam registradas na auditoria.">
                Status de Tratamento
            </SectionTitle>
            {!isEdit ? (
                <Typography color="text.secondary">Salve o prontuário antes de gerenciar o status de tratamento.</Typography>
            ) : (
                <Stack spacing={3}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary">Status atual:</Typography>
                        {(() => {
                            const o = STATUS_TRATAMENTO_OPCOES.find(x => x.v === statusTratamentoInicial);
                            if (!o) return <Chip label={statusTratamentoInicial || '—'} />;
                            return (
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderRadius: 2, bgcolor: o.bg }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: o.color }} />
                                    <Typography variant="body2" fontWeight={600} sx={{ color: o.color }}>{o.l}</Typography>
                                </Box>
                            );
                        })()}
                    </Stack>
                    <TextField
                        select fullWidth label="Novo Status" disabled={soLeitura || salvandoStatus}
                        value={statusTratamento}
                        onChange={(e) => setStatusTratamento(e.target.value)}
                        helperText={STATUS_TRATAMENTO_OPCOES.find(o => o.v === statusTratamento)?.desc || ''}
                    >
                        {STATUS_TRATAMENTO_OPCOES.map(o => (
                            <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>
                        ))}
                    </TextField>
                    {!soLeitura && (
                        <Box>
                            <Button
                                variant="contained"
                                disabled={salvandoStatus || statusTratamento === statusTratamentoInicial}
                                onClick={salvarStatusTratamento}
                                startIcon={salvandoStatus ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                                sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}
                            >
                                Atualizar Status
                            </Button>
                        </Box>
                    )}
                </Stack>
            )}
        </Box>
    );

    const SecaoEvolucoes = (
        <Box>
            <SectionTitle descricao="Registros de evolução das sessões vinculados ao prontuário.">Evoluções de Sessão</SectionTitle>
            {evolucoes.length === 0 ? (
                <Typography color="text.secondary">Nenhuma evolução registrada.</Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Registrado por</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {evolucoes.map(e => (
                            <TableRow key={e.id} hover>
                                <TableCell>{formatarData(e.dataRegistro)}</TableCell>
                                <TableCell>{e.registradoPor || '-'}</TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => setEvolucaoView(e)} title="Visualizar conteúdo">
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );

    return (
        <Box sx={{ flexGrow: 1, pb: soLeitura ? 4 : 10, px: isMobile ? 1 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/prontuarios')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                    {isView ? 'Visualizar Prontuário' : isEdit ? 'Editar Prontuário' : 'Novo Prontuário'}
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: '1px solid #eee', bgcolor: '#fafafa' }}>
                    <BuscaPaciente
                        label="Paciente *"
                        disabled={isEdit || soLeitura}
                        value={pacienteSelecionado}
                        onChange={(v) => { setPacienteSelecionado(v); setForm(f => ({ ...f, idPaciente: v?.id_paciente || null })); }}
                    />
                </Box>

                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    scrollButtons="auto"
                    sx={{
                        borderBottom: '1px solid #eee',
                        bgcolor: '#fafafa',
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 56 },
                        '& .Mui-selected': { color: '#1ABB9C !important' },
                        '& .MuiTabs-indicator': { backgroundColor: '#1ABB9C', height: 3 },
                    }}
                >
                    <Tab icon={<FolderSharedIcon />} iconPosition="start" label="Dados" />
                    <Tab icon={<LocalHospitalOutlinedIcon />} iconPosition="start" label="CID" />
                    <Tab icon={<FavoriteOutlinedIcon />} iconPosition="start" label="Tratamento" />
                    <Tab icon={<ContentPasteIcon />} iconPosition="start" label="Anamnese" />
                    <Tab icon={<EventAvailableIcon />} iconPosition="start" label="Evoluções" />
                </Tabs>

                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    {tab === 0 && SecaoDados}
                    {tab === 1 && SecaoCid}
                    {tab === 2 && SecaoTratamento}
                    {tab === 3 && SecaoAnamnese}
                    {tab === 4 && SecaoEvolucoes}
                </Box>
            </Paper>

            {!soLeitura && (
                <Paper elevation={3}
                    sx={{
                        position: 'fixed', bottom: 0, left: { xs: 0, md: 240 }, right: 0,
                        px: 3, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1,
                        bgcolor: '#fff', borderTop: '1px solid #eee', zIndex: 10,
                    }}>
                    <Button variant="outlined" onClick={() => navigate('/prontuarios')} sx={{ color: '#666', borderColor: '#ccc' }}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={solicitarSalvar} disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: 4 }}>
                        {isEdit ? 'Atualizar Prontuário' : 'Salvar Prontuário'}
                    </Button>
                </Paper>
            )}

            <Dialog open={!!evolucaoView} onClose={() => setEvolucaoView(null)} fullWidth maxWidth="md" fullScreen={isMobile}>
                <DialogTitle>Evolução — {evolucaoView ? formatarData(evolucaoView.dataRegistro) : ''}</DialogTitle>
                <DialogContent dividers>
                    {evolucaoView && (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Registrado por</Typography>
                                <Typography>{evolucaoView.registradoPor || '-'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: '#1ABB9C', fontWeight: 700 }}>Anotações</Typography>
                                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{evolucaoView.anotacoes || '—'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: '#1ABB9C', fontWeight: 700 }}>Técnicas Utilizadas</Typography>
                                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{evolucaoView.tecnicasUtilizadas || '—'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ color: '#1ABB9C', fontWeight: 700 }}>Planejamento da Próxima Sessão</Typography>
                                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{evolucaoView.planejamentoProximaSessao || '—'}</Typography>
                            </Box>
                            {evolucaoView.avaliacaoRisco && evolucaoView.avaliacaoRisco.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" sx={{ color: '#dc2626', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <WarningAmberIcon fontSize="small" /> Avaliações de Risco
                                    </Typography>
                                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                                        {evolucaoView.avaliacaoRisco.map(a => (
                                            <Paper key={a.id} variant="outlined" sx={{ p: 1.5 }}>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                                                    <Chip size="small" label={`Nível: ${a.nivelRisco || '-'}`} color={a.nivelRisco === 'CRITICO' || a.nivelRisco === 'ALTO' ? 'error' : a.nivelRisco === 'MODERADO' ? 'warning' : 'success'} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {a.dataAvaliacao ? new Date(a.dataAvaliacao).toLocaleDateString('pt-BR') : '-'}
                                                    </Typography>
                                                </Stack>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                                                    {a.ideacaoSuicida && <Chip size="small" label="Ideação Suicida" color="error" variant="outlined" />}
                                                    {a.planoSuicida && <Chip size="small" label="Plano Suicida" color="error" variant="outlined" />}
                                                    {a.ideacaoAutolesao && <Chip size="small" label="Ideação Autolesão" color="warning" variant="outlined" />}
                                                    {a.riscoParaTerceiros && <Chip size="small" label="Risco a Terceiros" color="warning" variant="outlined" />}
                                                </Stack>
                                                {a.condutaAdotada && <Typography variant="body2"><b>Conduta:</b> {a.condutaAdotada}</Typography>}
                                                {a.encaminhamentosRealizados && <Typography variant="body2"><b>Encaminhamentos:</b> {a.encaminhamentosRealizados}</Typography>}
                                                {a.redeSocialAcionada && <Typography variant="body2"><b>Rede social:</b> {a.redeSocialAcionada}</Typography>}
                                            </Paper>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEvolucaoView(null)}>Fechar</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
